import {
  Box,
  Button,
  Divider,
  ListItemText,
  Menu,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import React, { useEffect, useState } from "react";

export type ModelProvider = "ollama" | "openrouter";

interface ModelProviderMenuProps {
  open: boolean;
  anchorEl: HTMLElement | null;
  onClose: () => void;
  // Pre-populated values (when the user re-opens the menu after a
  // previous selection). Null/empty means "no provider chosen yet".
  provider: ModelProvider | null;
  model: string;
  onApply: (next: { provider: ModelProvider; model: string }) => void;
}

const PROVIDERS: { value: ModelProvider; label: string; description: string }[] = [
  {
    value: "ollama",
    label: "Ollama",
    description: "Modelli locali via Ollama",
  },
  {
    value: "openrouter",
    label: "OpenRouter",
    description: "Gateway multi-provider",
  },
];

/**
 * Small popup menu that lets the user pick the AI provider and the
 * model name to send to the backend. Opened by the konami-triggered
 * icon in <AiDrawer />'s header.
 *
 * The selection is "applied" only when the user clicks the "Applica"
 * button — choosing a provider in the list is just a preview, the
 * request body is updated only on confirm. This avoids sending a
 * half-configured request when the user is still picking.
 */
const ModelProviderMenu: React.FC<ModelProviderMenuProps> = ({
  open,
  anchorEl,
  onClose,
  provider,
  model,
  onApply,
}) => {
  // Local staging state — the user can pick / type freely without
  // mutating the real selection until they confirm with "Applica".
  const [stagedProvider, setStagedProvider] = useState<ModelProvider | null>(
    provider
  );
  const [stagedModel, setStagedModel] = useState<string>(model);

  // Re-sync staged state every time the menu opens, so the previous
  // selection is shown as a starting point.
  useEffect(() => {
    if (open) {
      setStagedProvider(provider);
      setStagedModel(model);
    }
  }, [open, provider, model]);

  const canApply = stagedProvider !== null && stagedModel.trim().length > 0;

  const handleApply = () => {
    if (!stagedProvider) return;
    onApply({ provider: stagedProvider, model: stagedModel.trim() });
  };

  return (
    <Menu
      open={open}
      anchorEl={anchorEl}
      onClose={onClose}
      anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      transformOrigin={{ vertical: "top", horizontal: "right" }}
      slotProps={{
        paper: {
          sx: { minWidth: 280, borderRadius: 2, mt: 0.5, p: 1 },
        },
      }}
    >
      <Box sx={{ px: 1, pt: 0.5, pb: 1 }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
          Provider &amp; modello
        </Typography>
        <Typography variant="caption" color="text.secondary">
          Scegli come instradare le richieste AI
        </Typography>
      </Box>
      <Divider sx={{ my: 0.5 }} />
      <Box sx={{ px: 1, pt: 0.5, pb: 0.5 }}>
        <Typography
          variant="caption"
          sx={{ fontWeight: 600, color: "text.secondary" }}
        >
          Provider
        </Typography>
      </Box>
      {PROVIDERS.map((p) => (
        <MenuItem
          key={p.value}
          selected={stagedProvider === p.value}
          onClick={() => setStagedProvider(p.value)}
          sx={{ borderRadius: 1, mx: 0.5 }}
        >
          <ListItemText
            primary={p.label}
            secondary={p.description}
            primaryTypographyProps={{ fontWeight: 500 }}
          />
        </MenuItem>
      ))}
      <Divider sx={{ my: 0.5 }} />
      <Box sx={{ px: 1, pt: 1, pb: 0.5 }}>
        <TextField
          autoFocus
          fullWidth
          size="small"
          label="Model"
          placeholder="es. llama3, gpt-4"
          value={stagedModel}
          onChange={(e) => setStagedModel(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && canApply) {
              e.preventDefault();
              handleApply();
            }
          }}
        />
      </Box>
      <Stack
        direction="row"
        justifyContent="flex-end"
        spacing={1}
        sx={{ px: 1, pt: 1, pb: 0.5 }}
      >
        <Button size="small" onClick={onClose} sx={{ textTransform: "none" }}>
          Annulla
        </Button>
        <Button
          size="small"
          variant="contained"
          onClick={handleApply}
          disabled={!canApply}
          sx={{ textTransform: "none" }}
        >
          Applica
        </Button>
      </Stack>
    </Menu>
  );
};

export default ModelProviderMenu;
